import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { formatProductTitle } from './formatProductTitle';
import { hrefForSameSiteAsset } from './productMediaUtils';
import { useFullscreenOverlay } from './useFullscreenOverlay';

const MAX_ZOOM = 4;
const STAGE_PAD = 12;

/** Fit image in box; never upscale past 100% native pixels at 1×. */
function computeFit(naturalW, naturalH, boxW, boxH) {
  if (!naturalW || !naturalH || !boxW || !boxH) return 1;
  return Math.min(boxW / naturalW, boxH / naturalH, 1);
}

export default function ImageModal({
  show,
  image,
  alt,
  onClose,
  onNext,
  onPrev,
  hasMultipleImages,
  currentIndex,
  totalImages,
}) {
  const [scale, setScale] = useState(1);
  const [isPinching, setIsPinching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const stageRef = useRef(null);
  const backdropRef = useFullscreenOverlay(show);
  const scaleRef = useRef(1);
  const pinchRef = useRef({ distance: 0, scale: 1, active: false });
  const lastTapRef = useRef(0);
  const imageSrc = useMemo(() => (image ? hrefForSameSiteAsset(image) : ''), [image]);

  const baseFit = useMemo(
    () => computeFit(naturalSize.w, naturalSize.h, stageSize.w - STAGE_PAD, stageSize.h - STAGE_PAD),
    [naturalSize, stageSize]
  );

  /** Cap zoom so we never stretch beyond native pixel density (keeps enlargement sharp). */
  const maxZoom = useMemo(() => {
    if (!naturalSize.w || !baseFit) return MAX_ZOOM;
    const nativeCap = 1 / baseFit;
    return Math.min(MAX_ZOOM, Math.max(1, nativeCap));
  }, [naturalSize.w, baseFit]);

  const clampScale = useCallback(
    (value) => Math.max(1, Math.min(value, maxZoom)),
    [maxZoom]
  );

  const setZoom = useCallback(
    (updater) => {
      setScale((prev) => clampScale(typeof updater === 'function' ? updater(prev) : updater));
    },
    [clampScale]
  );

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  useEffect(() => {
    setScale((prev) => Math.min(prev, maxZoom));
  }, [maxZoom]);

  const displaySize = useMemo(() => {
    if (!naturalSize.w || !naturalSize.h || !baseFit) return null;
    return {
      width: Math.round(naturalSize.w * baseFit * scale),
      height: Math.round(naturalSize.h * baseFit * scale),
    };
  }, [naturalSize, baseFit, scale]);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => prev + 0.25);
  }, [setZoom]);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.25, 1));
  }, [setZoom]);

  const resetStageView = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    stage.scrollLeft = 0;
    stage.scrollTop = 0;
  }, []);

  useEffect(() => {
    setScale(1);
    setLoading(true);
    setError(false);
    setNaturalSize({ w: 0, h: 0 });
    resetStageView();
  }, [imageSrc, resetStageView]);

  useEffect(() => {
    if (!show) return;
    setScale(1);
    resetStageView();
    requestAnimationFrame(resetStageView);
  }, [show, resetStageView]);

  /** Preload full-resolution asset as soon as the lightbox opens. */
  useEffect(() => {
    if (!show || !imageSrc) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = imageSrc;
    document.head.appendChild(link);
    const img = new Image();
    img.decoding = 'async';
    img.src = imageSrc;
    return () => {
      link.remove();
    };
  }, [show, imageSrc]);

  useEffect(() => {
    if (!show || !stageRef.current) return;
    const stage = stageRef.current;
    const measure = () => {
      const rect = stage.getBoundingClientRect();
      setStageSize({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [show, imageSrc]);

  useEffect(() => {
    if (!show || !stageRef.current) return;

    const el = stageRef.current;
    const onWheel = (e) => {
      e.preventDefault();
      setZoom((prevScale) => prevScale + (e.deltaY < 0 ? 0.25 : -0.25));
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [show, imageSrc, setZoom]);

  useEffect(() => {
    if (!show) return;
    const stage = stageRef.current;
    const backdrop = backdropRef.current;
    if (!stage || !backdrop) return;

    const touchDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        setIsPinching(true);
        pinchRef.current = {
          distance: touchDistance(e.touches),
          scale: scaleRef.current,
          active: true,
        };
        e.preventDefault();
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 2 && pinchRef.current.active) {
        e.preventDefault();
        const dist = touchDistance(e.touches);
        if (pinchRef.current.distance > 0) {
          const next = pinchRef.current.scale * (dist / pinchRef.current.distance);
          setZoom(next);
        }
      }
    };

    const onTouchEnd = (e) => {
      if (pinchRef.current.active && e.touches.length < 2) {
        pinchRef.current.active = false;
        pinchRef.current.scale = scaleRef.current;
        pinchRef.current.distance = 0;
        setIsPinching(false);
      }

      if (e.touches.length === 0 && !pinchRef.current.active) {
        const now = Date.now();
        if (now - lastTapRef.current < 320) {
          setZoom((prev) => {
            const next = prev > 1 ? 1 : Math.min(2, maxZoom);
            if (next === 1) requestAnimationFrame(resetStageView);
            return next;
          });
          lastTapRef.current = 0;
          e.preventDefault();
          return;
        }
        lastTapRef.current = now;
      }
    };

    const blockSafariGesture = (e) => e.preventDefault();

    const opts = { passive: false };
    stage.addEventListener('touchstart', onTouchStart, opts);
    stage.addEventListener('touchmove', onTouchMove, opts);
    stage.addEventListener('touchend', onTouchEnd);
    stage.addEventListener('touchcancel', onTouchEnd);
    backdrop.addEventListener('gesturestart', blockSafariGesture, opts);
    backdrop.addEventListener('gesturechange', blockSafariGesture, opts);
    backdrop.addEventListener('gestureend', blockSafariGesture, opts);

    return () => {
      stage.removeEventListener('touchstart', onTouchStart);
      stage.removeEventListener('touchmove', onTouchMove);
      stage.removeEventListener('touchend', onTouchEnd);
      stage.removeEventListener('touchcancel', onTouchEnd);
      backdrop.removeEventListener('gesturestart', blockSafariGesture);
      backdrop.removeEventListener('gesturechange', blockSafariGesture);
      backdrop.removeEventListener('gestureend', blockSafariGesture);
    };
  }, [show, imageSrc, backdropRef, resetStageView, setZoom, maxZoom]);

  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' && hasMultipleImages) {
        onNext?.();
      } else if (e.key === 'ArrowLeft' && hasMultipleImages) {
        onPrev?.();
      } else if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [show, hasMultipleImages, onNext, onPrev, onClose, handleZoomIn, handleZoomOut]);

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('image-modal-backdrop')) {
      onClose();
    }
  };

  const handleImageTap = () => {
    setZoom((prev) => {
      const next = prev > 1 ? 1 : Math.min(2, maxZoom);
      if (next === 1) requestAnimationFrame(resetStageView);
      return next;
    });
  };

  const handleImageLoad = (e) => {
    const img = e.currentTarget;
    setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
    setLoading(false);
    setError(false);
    resetStageView();
  };

  const displayTitle = formatProductTitle(alt || '');

  if (!show) return null;

  return createPortal(
    <div
      ref={backdropRef}
      className="image-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={displayTitle || 'Enlarged product image'}
      onClick={handleBackdropClick}
    >
      <div className="image-modal-shell" onClick={(e) => e.stopPropagation()}>
        <div className="image-modal-toolbar">
          <button
            type="button"
            className="image-modal-btn"
            onClick={handleZoomOut}
            disabled={scale <= 1}
            aria-label="Zoom out"
          >
            −
          </button>
          <button type="button" className="image-modal-btn" onClick={handleZoomIn} aria-label="Zoom in">
            +
          </button>
          <button type="button" className="image-modal-btn image-modal-btn--close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div
          className={`image-modal-stage${scale > 1 ? ' image-modal-stage--zoomed' : ''}`}
          ref={stageRef}
        >
          {hasMultipleImages && (
            <>
              <button
                type="button"
                className="image-modal-btn image-modal-btn--side image-modal-btn--prev"
                onClick={onPrev}
                aria-label="Previous image"
              >
                ❮
              </button>
              <button
                type="button"
                className="image-modal-btn image-modal-btn--side image-modal-btn--next"
                onClick={onNext}
                aria-label="Next image"
              >
                ❯
              </button>
            </>
          )}

          {loading && (
            <div className="image-modal-status image-modal-status--loading">Loading full image…</div>
          )}
          {error && (
            <div className="image-modal-status image-modal-status--error">Failed to load image</div>
          )}
          {!imageSrc && !loading && !error && (
            <div className="image-modal-status">No image available</div>
          )}

          {imageSrc ? (
            <div
              className={`image-modal-zoom-wrap${isPinching ? ' image-modal-zoom-wrap--pinching' : ''}${
                displaySize ? ' image-modal-zoom-wrap--sized' : ''
              }`}
            >
              <img
                key={imageSrc}
                src={imageSrc}
                alt={displayTitle || 'Product'}
                className={`image-modal-img${scale > 1 ? ' image-modal-img--zoomed' : ''}${
                  displaySize ? ' image-modal-img--sized' : ''
                }`}
                draggable={false}
                fetchPriority="high"
                decoding="async"
                onClick={handleImageTap}
                onError={() => {
                  setLoading(false);
                  setError(true);
                }}
                onLoad={handleImageLoad}
                style={
                  displaySize
                    ? { width: `${displaySize.width}px`, height: `${displaySize.height}px` }
                    : undefined
                }
              />
            </div>
          ) : null}

          {hasMultipleImages && totalImages > 1 && (
            <div className="image-modal-counter" aria-live="polite">
              {(currentIndex ?? 0) + 1} / {totalImages}
            </div>
          )}
        </div>

        {displayTitle ? <p className="image-modal-caption">{displayTitle}</p> : null}
      </div>
    </div>,
    document.body
  );
}
